import React, { useState, useRef } from 'react';
import { exportElementToPdf } from '../../lib/pdfExport';
import { generateScopeShieldEmail, generateScopeShieldMarkdown } from '../../lib/toolCalculations';

interface ScopeShieldProps {
  isUnlocked?: boolean;
  onOpenCheckout?: (tier?: 'single' | 'bundle') => void;
}

export const ScopeShield: React.FC<ScopeShieldProps> = ({
  isUnlocked = false,
  onOpenCheckout,
}) => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [clientName, setClientName] = useState('Sarah Jenkins');
  const [projectName, setProjectName] = useState('PayFlow Checkout Redesign');
  const [masterAgreementRef, setMasterAgreementRef] = useState('AGMT-2026-MSA-049');
  const [originalLaunchDate, setOriginalLaunchDate] = useState('October 16, 2026');
  const [revisedLaunchDate, setRevisedLaunchDate] = useState('October 21, 2026');
  const [baseScope, setBaseScope] = useState('Single-currency Stripe Elements checkout flow with webhook receipt trigger and responsive mobile layout.');
  const [clientMessage, setClientMessage] = useState(
    'Hey! Can we also add a merchant admin dashboard to search failed transactions and generate custom multi-currency PDF invoices before we launch on Friday? Should be super straightforward!'
  );
  const [estimatedDays, setEstimatedDays] = useState(3);
  const [addOnPrice, setAddOnPrice] = useState(2400);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedMd, setCopiedMd] = useState(false);
  const isWatermarked = !isUnlocked;

  // Pure Generated Response Email
  const generatedEmail = generateScopeShieldEmail({
    projectName,
    clientName,
    originalLaunchDate,
    revisedLaunchDate,
    masterAgreementRef,
    addOnPrice,
  });

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(generatedEmail);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const handleCopyMarkdown = () => {
    const md = generateScopeShieldMarkdown({
      projectName,
      clientName,
      masterAgreementRef,
      originalLaunchDate,
      revisedLaunchDate,
      baseScope,
      clientMessage,
      estimatedDays,
      addOnPrice,
      isWatermarked,
    });
    navigator.clipboard.writeText(md);
    setCopiedMd(true);
    setTimeout(() => setCopiedMd(false), 2000);
  };

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      await exportElementToPdf(documentRef.current, {
        filename: `Change-Order-Addendum-01-${projectName.replace(/[^a-zA-Z0-9_-]/g, '-')}-REF-2026-CO-0104.pdf`,
        documentTitle: `Change Order Addendum #01 · ${projectName}`,
        isUnlocked,
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      window.print();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Top Banner (Hidden in PDF / Print) */}
      <div className="no-print screen-only flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#232736] pb-6">
        <div>
          <div className="text-[11px] font-mono text-sky-400 uppercase tracking-widest flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
            TOOL 02 // SCOPE SHIELD &amp; CHANGE-ORDER GENERATOR
          </div>
          <h2 className="font-tech text-3xl font-bold text-white mt-1">
            Scope Creep Defense &amp; Change Order Generator
          </h2>
          <p className="text-xs text-[#8b92a5] font-mono mt-1">
            Turn stressful, unpaid client requests into firm, professional replies and signed paid addendums.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isUnlocked ? (
            <span className="px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] font-bold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              PRO UNLOCKED
            </span>
          ) : (
            <button
              onClick={() => onOpenCheckout?.('single')}
              className="px-2.5 py-1.5 rounded bg-sky-400 hover:bg-sky-300 text-black font-mono text-[10px] font-bold transition flex items-center gap-1.5 shadow-[0_0_10px_rgba(56,189,248,0.2)]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              UNLOCK PRO ($9)
            </button>
          )}
          <button
            onClick={handleCopyEmail}
            className="px-3 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-white rounded transition flex items-center gap-1.5"
          >
            {copiedEmail ? '✓ Copied Email' : 'Copy Email Response'}
          </button>
          <button
            onClick={handleCopyMarkdown}
            className="px-3 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-white rounded transition flex items-center gap-1.5"
          >
            {copiedMd ? '✓ Copied Addendum MD' : 'Copy Addendum MD'}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={isExporting}
            className="px-3.5 py-1.5 bg-sky-400 hover:bg-sky-300 disabled:opacity-50 text-black font-tech font-bold text-xs rounded transition flex items-center gap-1.5 shadow-[0_0_12px_rgba(56,189,248,0.25)]"
          >
            {isExporting ? (
              <>
                <span className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isUnlocked ? 'Download Vector Addendum PDF' : 'Download Addendum (Free)'}
              </>
            )}
          </button>
          <button
            onClick={() => window.print()}
            title="Print to physical printer"
            className="px-2.5 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-[#8b92a5] hover:text-white rounded transition flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H9v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Input Column */}
        <div className="lg:col-span-5 space-y-5 no-print">
          <div className="blueprint-card rounded-xl p-4 border border-[#232736] flex items-center justify-between font-mono text-xs">
            <span className="text-[11px] text-[#8b92a5] uppercase">Vector PDF Status:</span>
            {isUnlocked ? (
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold">
                Pro Unbranded (Active)
              </span>
            ) : (
              <button
                onClick={() => onOpenCheckout?.('single')}
                className="text-[10px] px-2 py-0.5 rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 transition font-bold"
              >
                Watermarked · Unlock Pro ($9)
              </button>
            )}
          </div>
          <div className="blueprint-card rounded-xl p-5 border border-[#232736] space-y-4">
            <h3 className="font-tech text-base font-bold text-white border-b border-[#232736] pb-2">
              Inbound Scope Details
            </h3>

            <div className="space-y-3 font-mono text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                  Original Agreed Baseline Scope
                </label>
                <textarea
                  rows={2}
                  value={baseScope}
                  onChange={(e) => setBaseScope(e.target.value)}
                  className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 resize-none"
                />
              </div>

              <div>
                <label className="text-[10px] text-amber-400 uppercase block mb-1 font-bold">
                  Client's Out-Of-Scope Request (Paste Slack/Email)
                </label>
                <textarea
                  rows={3}
                  value={clientMessage}
                  onChange={(e) => setClientMessage(e.target.value)}
                  className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-amber-500/40 text-white focus:outline-none focus:border-amber-400 resize-none text-[11px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 border-t border-[#232736] pt-3">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Master Agreement Ref
                  </label>
                  <input
                    type="text"
                    value={masterAgreementRef}
                    onChange={(e) => setMasterAgreementRef(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Add-On Price ($)
                  </label>
                  <input
                    type="number"
                    value={addOnPrice}
                    onChange={(e) => setAddOnPrice(Number(e.target.value))}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Original Launch Date
                  </label>
                  <input
                    type="text"
                    value={originalLaunchDate}
                    onChange={(e) => setOriginalLaunchDate(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Revised Launch (+{estimatedDays}d)
                  </label>
                  <input
                    type="text"
                    value={revisedLaunchDate}
                    onChange={(e) => setRevisedLaunchDate(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#232736] pt-3">
                <span className="text-[11px] text-[#8b92a5] uppercase">Watermark:</span>
                {isUnlocked ? (
                  <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold">
                    Pro (Removed)
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-zinc-800 text-zinc-400">
                    Watermarked (Free)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick Guidance Box */}
          <div className="p-4 rounded-xl bg-[#10121a] border border-[#232736] font-mono text-xs space-y-1.5 text-zinc-400">
            <div className="text-[10px] text-amber-400 uppercase font-bold">
              THE ZERO-APOLOGY RULE
            </div>
            <p className="text-[11px] leading-relaxed">
              Never start your reply with *"Sorry, but that's out of scope"*. Acknowledge the feature's value, present the two business choices, and attach the official Change Order PDF.
            </p>
          </div>
        </div>

        {/* Live Output Preview (Email & Change Order PDF) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Email Preview (Screen Only) */}
          <div className="screen-only blueprint-card rounded-2xl p-6 border border-[#232736] space-y-4 bg-[#0d1017]">
            <div className="flex items-center justify-between border-b border-[#232736] pb-3 text-xs font-mono">
              <span className="text-sky-400 font-bold">GENERATED CLIENT DEFENSE EMAIL</span>
              <span className="text-zinc-500">READY TO SEND</span>
            </div>
            <pre className="text-xs font-mono text-zinc-200 whitespace-pre-wrap leading-relaxed bg-[#10121a] p-4 rounded-xl border border-[#232736]">
              {generatedEmail}
            </pre>
          </div>

          {/* Formal Change Order Addendum Preview (Print & Screen) */}
          <div ref={documentRef} className="print-content blueprint-card rounded-2xl p-6 sm:p-8 border border-sky-500/30 space-y-6 bg-[#0c0e14]">
            {/* Executive Document Header & Letterhead */}
            <div className="print-doc-header border-b border-[#232736] pb-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-[10px] font-mono text-amber-400 uppercase tracking-widest font-bold block">
                    LEGAL INSTRUMENT // CONTRACT ADDENDUM #01
                  </span>
                  <h3 className="font-tech text-2xl sm:text-3xl font-bold text-white mt-0.5">
                    Official Change Order &amp; Schedule Recalibration
                  </h3>
                </div>
                <div className="text-right font-mono text-xs">
                  <span className="text-[10px] text-[#8b92a5] uppercase block">DOCUMENT REF</span>
                  <span className="text-white font-bold tracking-wider">REF-2026-CO-0104</span>
                </div>
              </div>

              {/* Firm Expiration Notice Banner */}
              <div className="px-3.5 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-between text-xs font-mono">
                <span className="text-amber-400 font-bold uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  FIRM 48-HOUR EXPIRATION CUTOFF
                </span>
                <span className="text-zinc-300 text-[11px]">
                  Lapses 48h Post-Issuance at 5:00 PM EST
                </span>
              </div>

              {/* Metadata Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-[#232736] text-[10px] font-mono text-[#8b92a5]">
                <div>
                  <span className="block text-zinc-500 uppercase">Effective Date:</span>
                  <span className="text-zinc-300 font-bold">{new Date().toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Master Contract:</span>
                  <span className="text-zinc-300 font-bold">{masterAgreementRef}</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Project Name:</span>
                  <span className="text-zinc-300 font-bold">{projectName}</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Execution Terms:</span>
                  <span className="text-amber-400 font-bold">100% Upfront Settlement</span>
                </div>
              </div>

              {/* Parties Summary Box */}
              <div className="p-3.5 rounded-xl bg-[#10121a] border border-[#232736] grid grid-cols-2 gap-4 text-xs font-mono">
                <div>
                  <span className="text-[10px] text-zinc-500 uppercase block font-bold">CLIENT ENTITY:</span>
                  <span className="text-white font-bold block">{clientName}</span>
                  <span className="text-[11px] text-[#8b92a5]">Authorized Project Sponsor</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-zinc-500 uppercase block font-bold">PRIME CONTRACTOR:</span>
                  <span className="text-white font-bold block">Studio Principal / Engineering Lead</span>
                  <span className="text-[11px] text-sky-400">Systems &amp; Architecture Practice</span>
                </div>
              </div>
            </div>

            {/* 3-Column Scope Reconciliation Table */}
            <div className="print-card print-avoid-break space-y-2">
              <span className="text-[10px] font-mono text-sky-400 uppercase tracking-wider font-bold block">
                SCOPE RECONCILIATION &amp; IMPACT MATRIX
              </span>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#232736] text-[10px] font-mono text-[#8b92a5]">
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">ORIGINAL AGREED SCOPE (EXHIBIT A)</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">REQUESTED EXPANSION DELTA</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">SCHEDULE &amp; COMMERCIAL IMPACT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] text-zinc-300 align-top font-sans text-xs leading-relaxed">
                      {baseScope}
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] text-amber-300 italic align-top font-sans text-xs leading-relaxed">
                      "{clientMessage}"
                    </td>
                    <td className="py-2.5 px-3 border border-[#232736] text-white font-bold align-top shrink-0 font-mono text-xs">
                      <div>+{estimatedDays} Business Days</div>
                      <div className="text-sky-400">+${addOnPrice.toLocaleString()} USD</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Revised Milestone & Delivery Schedule Table */}
            <div className="print-card print-avoid-break space-y-2">
              <span className="text-[10px] font-mono text-amber-400 uppercase tracking-wider font-bold block">
                REVISED MILESTONE &amp; DELIVERY TIMELINE
              </span>
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="border-b border-[#232736] text-[10px] text-[#8b92a5]">
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">PROJECT MILESTONE</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">ORIGINAL TARGET</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">RECALIBRATED CUTOVER</th>
                    <th className="text-left py-2 px-3 bg-[#10121a] border border-[#232736]">CALENDAR VARIANCE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#232736]">
                    <td className="py-2.5 px-3 border border-[#232736] text-white font-bold">Staging Handover &amp; Cutover</td>
                    <td className="py-2.5 px-3 border border-[#232736] text-zinc-300">{originalLaunchDate}</td>
                    <td className="py-2.5 px-3 border border-[#232736] text-amber-300 font-bold">{revisedLaunchDate}</td>
                    <td className="py-2.5 px-3 border border-[#232736] text-sky-400 font-bold">+{estimatedDays} Business Days</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Numbered Enforceable Legal Articles */}
            <div className="print-card print-avoid-break space-y-3 font-sans text-xs text-zinc-300 leading-relaxed border-t border-[#232736] pt-4">
              <p>
                <strong className="text-white font-mono text-[11px] block mb-0.5">1. SCOPE AUTHORIZATION &amp; SPECIFICATION:</strong>
                The Client hereby authorizes Contractor to incorporate the expansion deliverables detailed in Column 2 above into the active production sprint milestone.
              </p>
              <p>
                <strong className="text-white font-mono text-[11px] block mb-0.5">2. COMPENSATION &amp; 100% UPFRONT SETTLEMENT:</strong>
                In consideration of the additional engineering labor and reserved production availability, Client agrees to pay the supplemental fee of <strong>${addOnPrice.toLocaleString()} USD</strong>, payable 100% upon execution of this Change Order prior to development initiation.
              </p>
              <p>
                <strong className="text-white font-mono text-[11px] block mb-0.5">3. DAY-FOR-DAY SCHEDULE RECALIBRATION &amp; WAIVER:</strong>
                Delivery deadlines set forth in Exhibit A are hereby extended by <strong>{estimatedDays} business days</strong> to <strong>{revisedLaunchDate}</strong>. Client expressly waives any claims for delay penalties, milestone regressions, or consequential damages resulting from this schedule shift.
              </p>
              <p>
                <strong className="text-white font-mono text-[11px] block mb-0.5">4. RATIFICATION OF MASTER AGREEMENT &amp; INTEGRATION RIDER:</strong>
                Except as expressly modified by this Change Order #01, all terms, conditions, intellectual property covenants, and liability limitations in the Underlying Master Services Agreement (<strong>{masterAgreementRef}</strong>) remain ratified, unamended, and in full legal force and effect.
              </p>
              <p className="text-[11px] text-amber-400 italic font-mono pt-1">
                *EXPIRATION MANDATE: This Change Order constitutes a firm, time-delimited offer expiring 48 hours post-issuance. In the event of non-execution, work proceeds strictly under original Exhibit A deliverables.
              </p>
            </div>

            {/* Signatures */}
            <div className="print-signature-block print-avoid-break grid grid-cols-2 gap-6 pt-5 border-t border-[#232736] font-mono">
              <div className="space-y-2">
                <span className="text-[10px] text-zinc-500 uppercase block font-bold">CLIENT ACCEPTANCE:</span>
                <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                  {clientName}
                </div>
                <span className="text-[10px] text-zinc-600 block">AUTHORIZED SIGNATORY &amp; DATE</span>
              </div>
              <div className="space-y-2">
                <span className="text-[10px] text-zinc-500 uppercase block font-bold">STUDIO ACCEPTANCE:</span>
                <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                  Studio Principal
                </div>
                <span className="text-[10px] text-zinc-600 block">ACCEPTED &amp; EXECUTED</span>
              </div>
            </div>

            {/* Watermark / Print Footer */}
            {isWatermarked && (
              <div className="print-doc-footer border-t border-[#232736] pt-3 text-right text-[10px] font-mono text-sky-400/80">
                Generated with Bureau // Scope Shield · Pro Unlocks Unbranded Vector PDF Export
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
